import React, { useState, useEffect } from 'react';
import { Formik, Form, useFormikContext } from 'formik';

import Box from '@mui/material/Box';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import Grid from '@mui/material/Grid';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import { TextFieldCustom } from '../helpers/formFields';
import { formatNumberThsndAndDec } from '../helpers/stringFormat';
import { formatString } from '../value/valueFunctions';
import { makePlural } from '../helpers/stringFormat';

// Helper to generate initial variables values from a variables array
const getInitialVariables = (variables) => {
    return variables.reduce((acc, variable) => {
        acc[variable.name] = {
            id: variable.id,
            value: formatNumberThsndAndDec(variable.value)
        };
        return acc;
    }, {});
};

// This component renders the variables part using the parent's Formik context.
const VariablesFormSection = ({ variables }) => {
    const { values, errors, touched, handleBlur, setFieldValue } = useFormikContext();

    const renderVariables = (variable, index) => (
        <React.Fragment key={variable.name}>
            {index === 0 && (
                <>
                    <Grid item xs={6} />
                    <Grid item xs={6}>
                        <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                            {formatString(variable.category)}
                        </Typography>
                    </Grid>
                </>
            )}
            <Grid item xs={6}>
                <Typography variant="caption">
                    {formatString(variable.name)}
                </Typography>
            </Grid>
            <Grid item xs={6}>
                <TextFieldCustom
                    name={`variables.${variable.name}.value`}
                    startAdornment={variable.unit === '€' ? variable.unit : null}
                    endAdornment={
                        variable.unit !== '€' ? (
                            <Typography variant="caption">
                                {makePlural(variable.unit)}
                            </Typography>
                        ) : null
                    }
                    value={values.variables[variable.name]?.value || ''}
                    onChange={(event) => {
                        const { value } = event.target;
                        const formattedValue = formatNumberThsndAndDec(value);
                        // setFieldValue(`variables.${variable.name}.value`, formattedValue || '');
                        const current = values.variables[variable.name] || {};
                        setFieldValue(`variables.${variable.name}`, {
                            ...current,
                            value: formattedValue || '',
                        });
                    }}
                    onBlur={handleBlur}
                    error={
                        touched.variables &&
                        touched.variables[variable.name] &&
                        errors.variables &&
                        errors.variables[variable.name] &&
                        Boolean(errors.variables[variable.name].value)
                    }
                    helperText={
                        touched.variables &&
                        touched.variables[variable.name] &&
                        errors.variables &&
                        errors.variables[variable.name]?.value
                    }
                />
            </Grid>
        </React.Fragment>
    );

    const categoryList = ['general', 'labor', 'operating', 'inventory', 'investment', 'marketing and sales'];

    return (
        <Box sx={{ maxHeight: 380, overflowY: 'auto', pr: 2 }}>
            <Grid container rowGap={2}>
                {variables.filter(variable => variable.category === 'general').map(renderVariables)}
                {variables.filter(variable => variable.category === 'labor').map(renderVariables)}
                {variables.filter(variable => variable.category === 'operating').map(renderVariables)}
                {variables.filter(variable => variable.category === 'inventory').map(renderVariables)}
                {variables.filter(variable => variable.category === 'investment').map(renderVariables)}
                {variables.filter(variable => variable.category === 'marketing and sales').map(renderVariables)}
                {variables.filter(variable => !categoryList.includes(variable.category)).map(renderVariables)}
            </Grid>
        </Box>
    );
};

// This component handles the model-based effect using Formik context.
const ModelEffect = ({ valueModels, activeStep, editSegmentId, editSegment }) => {
    const { values, setFieldValue } = useFormikContext();

    useEffect(() => {
        // Only reset variables when we are on step 1 and a model is selected.
        if (activeStep === 1 && values.value_model_id) {
            const varsForModel = valueModels.find(valueModel => valueModel.id === values.value_model_id);
            if (varsForModel && Array.isArray(varsForModel.variables) && !editSegmentId) {
                const initialVars = getInitialVariables(varsForModel.variables);
                setFieldValue('variables', initialVars);
            }
            else if (editSegmentId) {
                const formattedVariables = Object.fromEntries(
                    Object.entries(editSegment["variables"] || {}).map(([key, variable]) => [
                        key,
                        {
                            ...variable,
                            value: formatNumberThsndAndDec(variable.value)
                        }
                    ])
                );

                setFieldValue("variables", formattedVariables);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStep, values.value_model_id, setFieldValue, valueModels], editSegmentId);

    useEffect(() => {
        if (editSegmentId) {
            setFieldValue("name", editSegment["name"]);
            setFieldValue("value_model_id", editSegment["value_model_id"]);
            setFieldValue("variables", editSegment["variables"]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editSegmentId])

    return null;
};

// This is the multi‐step form component.
const SegmentCreateForm = ({ valueModels, editSegmentId, editSegment, onSubmit }) => {
    const [activeStep, setActiveStep] = useState(0);

    const initialValues = {
        name: '',
        value_model_id: valueModels[0]?.id || '',
        variables: {} // will be set once a model is chosen
    };

    return (
        <Formik
            initialValues={initialValues}
            enableReinitialize
            onSubmit={(values) => {
                // All form data: name, model, and variables will be available here.
                onSubmit(values);
            }}
        >
            {({ values, handleChange, handleBlur, touched, errors, setFieldValue }) => (
                <Form>
                    {/* ModelEffect is called here at the top level of the Formik context */}
                    <ModelEffect valueModels={valueModels} editSegmentId={editSegmentId} editSegment={editSegment} activeStep={activeStep} />
                    {activeStep === 0 && (
                        <Box sx={{ maxWidth: 350 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Stack direction="column">
                                        <Typography variant="caption">Name</Typography>
                                        <TextFieldCustom
                                            name="name"
                                            value={values.name}
                                            onChange={handleChange}
                                            size="small"
                                            onBlur={handleBlur}
                                            error={touched.name && Boolean(errors.name)}
                                            helperText={touched.name && errors.name}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12}>
                                    <Stack direction="column">
                                        <Typography variant="caption">Select Value Model</Typography>
                                        <Select
                                            fullWidth
                                            name="value_model_id"
                                            value={values.value_model_id}
                                            size="small"
                                            displayEmpty
                                            onChange={(e) => setFieldValue('value_model_id', e.target.value)}
                                        >
                                            {valueModels.map((model) => (
                                                <MenuItem key={model.id} value={model.id}>
                                                    {model.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            onClick={() => setActiveStep(1)}
                                            disabled={!values.name || !values.value_model_id}
                                            endIcon={<ChevronRightRoundedIcon />}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Next
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                    {activeStep === 1 && (
                        <Box sx={{ maxWidth: 450 }}>
                            {valueModels.find(valueModel => valueModel.id === values.value_model_id) && (
                                <VariablesFormSection
                                    key={values.value_model_id} // <-- This key forces remounting when the model changes
                                    variables={
                                        valueModels.find(valueModel => valueModel.id === values.value_model_id).variables
                                    }
                                />
                            )}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                                <Button
                                    onClick={() => setActiveStep(0)}
                                    startIcon={<ChevronLeftRoundedIcon />}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Back
                                </Button>
                                <Button
                                    variant="contained"
                                    type="submit"
                                    sx={{ textTransform: 'none' }}
                                >
                                    Save
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Form>
            )}
        </Formik>
    );
};

export default SegmentCreateForm;
